// frontend/src/internal/InternalStaffBanner.js
// 自社スタッフが既存ページを閲覧している間、画面上部に常時表示するバナー。
// - どの組織を閲覧中か / 閲覧 or 編集モード / 残り時間 (編集時)
// - 編集 ON ボタン (理由入力モーダル直接呼び出し)
// - 編集 OFF / 組織選択へ ボタン

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useInternalStaff } from '../contexts/InternalStaffContext';

const InternalStaffBanner = () => {
  const location = useLocation();
  const {
    internalStaff, selectedOrg, editSession, canEdit,
    startEditMode, endEditMode,
  } = useInternalStaff();
  const [remainingTime, setRemainingTime] = useState('');
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    if (!editSession) { setRemainingTime(''); return; }
    const update = () => {
      const remainMs = new Date(editSession.expires_at).getTime() - Date.now();
      if (remainMs <= 0) { setRemainingTime('期限切れ'); return; }
      const min = Math.floor(remainMs / 60000);
      const sec = Math.floor((remainMs % 60000) / 1000);
      setRemainingTime(`${min}:${String(sec).padStart(2,'0')}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [editSession]);

  // 内部スタッフでない、または internal ページ自身では表示しない
  if (!internalStaff || !selectedOrg) return null;
  if (location.pathname.startsWith('/internal/')) return null;

  const handleStartEdit = async () => {
    if (!reason.trim()) {
      setModalError('作業理由は必須です (依頼番号 / 内容など)');
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      await startEditMode(reason);
      setShowReasonModal(false);
      setReason('');
    } catch (e) {
      setModalError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canRequestEdit = internalStaff.role !== 'viewer';

  return (
    <>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: '32px',
        boxSizing: 'border-box',
        zIndex: 9999,
        background: canEdit
          ? 'linear-gradient(90deg, #dc2626, #b91c1c)'
          : 'linear-gradient(90deg, #1d4ed8, #1e40af)',
        color: 'white',
        padding: '0 16px',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span style={{ fontWeight: 700 }}>
            🔧 Internal: {canEdit ? '⚠️ 編集中' : '閲覧中'}
          </span>
          <span>
            {selectedOrg.name || selectedOrg.id} / {internalStaff.name} ({internalStaff.role})
          </span>
          {canEdit && (
            <span style={{
              padding: '2px 8px', background: 'rgba(255,255,255,0.2)',
              borderRadius: '10px', fontFamily: 'monospace',
            }}>
              残り {remainingTime}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* 編集モード ON ボタン (viewer 以外で、 編集中じゃない時のみ表示) */}
          {!canEdit && canRequestEdit && (
            <button onClick={() => { setReason(''); setModalError(''); setShowReasonModal(true); }} style={{
              padding: '3px 10px', background: 'rgba(255,255,255,0.95)',
              color: '#b91c1c', border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
              fontWeight: 700,
            }}>
              ⚠️ 編集 ON
            </button>
          )}
          {canEdit && (
            <button onClick={endEditMode} style={{
              padding: '3px 10px', background: 'rgba(255,255,255,0.25)',
              color: 'white', border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
            }}>
              編集 OFF
            </button>
          )}
          <button onClick={() => {
            // 組織選択画面に戻る (偽装 supabase_user をクリアしてハードリロード)
            localStorage.removeItem('supabase_user');
            localStorage.removeItem('currentOrganization');
            window.location.href = '/internal/select-org';
          }} style={{
            padding: '3px 10px', background: 'rgba(255,255,255,0.25)',
            color: 'white', border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
          }}>
            組織選択へ
          </button>
        </div>
      </div>

      {/* 作業理由入力モーダル */}
      {showReasonModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000, padding: '20px',
        }}>
          <div style={{
            background: 'white', borderRadius: '12px',
            padding: '24px', maxWidth: '480px', width: '100%',
          }}>
            <h2 style={{ margin: 0, marginBottom: 10, fontSize: '18px', color: '#0f172a' }}>編集モードを ON にする</h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 14px' }}>
              作業理由 (リモート依頼番号・作業内容など) を入力してください。
              編集モードは <strong>2 時間</strong>で自動的に OFF になります。
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="例: REQ-2026-0123 / シフトパターン設定の修正"
              rows={4}
              autoFocus
              style={{
                width: '100%', padding: '10px 12px',
                borderRadius: '8px', border: '1px solid #d1d5db',
                fontSize: '13px', resize: 'vertical', boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
            {modalError && (
              <div style={{
                background: '#fee2e2', color: '#7f1d1d',
                padding: '8px 12px', borderRadius: '8px',
                fontSize: '12px', marginTop: 10,
              }}>{modalError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowReasonModal(false); setReason(''); setModalError(''); }}
                disabled={submitting}
                style={{
                  padding: '8px 16px', background: '#e5e7eb',
                  border: 'none', borderRadius: '8px',
                  fontSize: '13px', cursor: submitting ? 'not-allowed' : 'pointer',
                }}>
                キャンセル
              </button>
              <button onClick={handleStartEdit} disabled={submitting}
                style={{
                  padding: '8px 16px',
                  background: submitting ? '#94a3b8' : '#dc2626',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontWeight: 700, fontSize: '13px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}>
                {submitting ? '開始中...' : '編集モードを ON'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InternalStaffBanner;
